import { useState, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { getClass, getBackground, findWeaponByName, isAnyWeaponToken } from '@character-creator/data';
import { useCharacterCreationStore } from '../../../stores/characterCreationStore';

// Format equipment item ID to display name
function formatEquipmentName(id: string): string {
  return id
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Check if an equipment item is a weapon (or a weapon category token)
function isWeapon(itemId: string): boolean {
  return findWeaponByName(itemId) != null || isAnyWeaponToken(itemId);
}

interface EquipmentChoice {
  choose: number;
  from: string[][];
}

type StartingEquipmentItem = string | EquipmentChoice;

function isEquipmentChoice(item: StartingEquipmentItem): item is EquipmentChoice {
  return typeof item !== 'string' && 'choose' in item;
}

export function EquipmentStep() {
  const { draft, setEquipmentChoices, addCustomEquipmentItem, removeCustomEquipmentItem } = useCharacterCreationStore();
  const [selections, setSelections] = useState<Record<number, number>>({});
  const [newItemInput, setNewItemInput] = useState('');

  const selectedClass = draft.classId ? getClass(draft.classId) : null;
  const selectedBackground = draft.backgroundId ? getBackground(draft.backgroundId) : null;
  const isCommoner = draft.confirmedCommoner && !draft.classId;

  // Get equipment choices that need user selection (excluding weapons)
  const { equipmentChoices, fixedEquipment } = useMemo(() => {
    const choices: { index: number; choice: EquipmentChoice }[] = [];
    const fixed: string[] = [];

    if (selectedClass) {
      selectedClass.startingEquipment.forEach((item, index) => {
        if (isEquipmentChoice(item)) {
          // Check if this choice contains any non-weapon options
          const hasNonWeaponOptions = item.from.some((optionGroup) =>
            optionGroup.some((itemId) => !isWeapon(itemId))
          );
          if (hasNonWeaponOptions) {
            choices.push({ index, choice: item });
          }
        } else {
          // Only add non-weapon fixed equipment
          if (!isWeapon(item)) {
            fixed.push(item);
          }
        }
      });
    }

    return { equipmentChoices: choices, fixedEquipment: fixed };
  }, [selectedClass]);

  // Memoize consolidated equipment to avoid recomputing in both columns
  const consolidatedFixed = useMemo(() => consolidateEquipment(fixedEquipment), [fixedEquipment]);

  // Initialize selections from store or defaults
  useEffect(() => {
    if (draft.equipmentSelections) {
      setSelections(draft.equipmentSelections);
    } else {
      // Default to first option for each choice
      const defaults: Record<number, number> = {};
      equipmentChoices.forEach(({ index }) => {
        defaults[index] = 0;
      });
      setSelections(defaults);
    }
  }, [draft.equipmentSelections, equipmentChoices]);

  // Update store when selections change
  const handleSelectionChange = (choiceIndex: number, optionIndex: number) => {
    const newSelections = { ...selections, [choiceIndex]: optionIndex };
    setSelections(newSelections);
    setEquipmentChoices(newSelections);
  };

  // Handle adding custom equipment
  const handleAddCustomItem = () => {
    if (newItemInput.trim()) {
      addCustomEquipmentItem(newItemInput);
      setNewItemInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomItem();
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
          Gear & Equipment
        </h2>
        <p className="text-text-secondary">
          Choose your armor, tools, adventuring gear, and supplies. Weapons & attacks are configured on the Weapons & Attacks sub-tab.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left Column - Equipment Choices */}
        <div className="space-y-6">
          {/* Class Equipment */}
          {selectedClass && (
            <div className="card p-6">
              <h3 className="font-semibold text-accent-gold mb-4">
                {selectedClass.name} Starting Equipment
              </h3>

              {/* Equipment Choices */}
              {equipmentChoices.length > 0 && (
                <div className="space-y-4 mb-4">
                  {equipmentChoices.map(({ index, choice }) => (
                    <EquipmentChoiceSelector
                      key={index}
                      choice={choice}
                      selectedIndex={selections[index] ?? 0}
                      onChange={(optionIndex) => handleSelectionChange(index, optionIndex)}
                    />
                  ))}
                </div>
              )}

              {/* Fixed Equipment */}
              {fixedEquipment.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-text-muted mb-2">
                    Also included:
                  </h4>
                  <ul className="space-y-1 text-sm text-text-secondary">
                    {consolidatedFixed.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-accent-gold">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {isCommoner && (
            <div className="card p-6">
              <h3 className="font-semibold text-accent-gold mb-4">Commoner Equipment</h3>
              <p className="text-sm text-text-muted">
                As a commoner, you start with no class equipment. You may receive equipment
                from your background, or start with nothing.
              </p>
            </div>
          )}

          {/* Background Equipment */}
          {selectedBackground && (
            <div className="card p-6">
              <h3 className="font-semibold text-text-primary mb-4">
                {selectedBackground.name} Background Equipment
              </h3>
              <ul className="space-y-1 text-sm text-text-secondary">
                {selectedBackground.equipment.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-accent-gold">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Custom Equipment */}
          <div className="card p-6">
            <h3 className="font-semibold text-text-primary mb-4">
              Custom Equipment
            </h3>
            <p className="text-sm text-text-muted mb-4">
              Add homebrew items, GM-granted equipment, or other items not listed above.
              Include quantities in the name (e.g., "5x Healing Potion").
            </p>

            {/* Input for new item */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newItemInput}
                onChange={(e) => setNewItemInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., 3x Torch, Rope (50 ft)..."
                className="flex-1 px-3 py-2 rounded-lg border border-border-default bg-background-secondary text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:border-accent-gold"
              />
              <button
                type="button"
                onClick={handleAddCustomItem}
                disabled={!newItemInput.trim()}
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium text-sm transition-all',
                  newItemInput.trim()
                    ? 'bg-accent-gold text-background-primary hover:bg-accent-gold/80'
                    : 'bg-background-elevated text-text-muted cursor-not-allowed'
                )}
              >
                Add
              </button>
            </div>

            {/* List of custom items */}
            {(draft.customEquipment?.length ?? 0) > 0 ? (
              <ul className="space-y-2">
                {(draft.customEquipment ?? []).map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between gap-2 p-2 rounded-lg bg-background-secondary"
                  >
                    <span className="text-sm text-text-primary">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeCustomEquipmentItem(index)}
                      className="text-text-muted hover:text-error transition-colors p-1"
                      aria-label={`Remove ${item}`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-muted italic">
                No custom items added yet.
              </p>
            )}
          </div>

          {!selectedClass && !isCommoner && !selectedBackground && (
            <div className="card p-6 text-center text-text-muted">
              <p>Select a class and/or background to see starting equipment.</p>
            </div>
          )}
        </div>

        {/* Right Column - Equipment Summary */}
        <div>
          <div className="card p-6 sticky top-4">
            <h3 className="font-semibold text-text-primary mb-4">Equipment Summary</h3>

            {(selectedClass || selectedBackground || (draft.customEquipment?.length ?? 0) > 0) ? (
              <div className="space-y-4">
                {/* Selected Equipment from Choices (non-weapons only) */}
                {equipmentChoices.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-accent-gold mb-2">
                      Your Choices
                    </h4>
                    <ul className="space-y-1 text-sm text-text-primary">
                      {equipmentChoices.map(({ index, choice }) => {
                        const selectedOption = choice.from[selections[index] ?? 0];
                        const nonWeaponItems = selectedOption.filter((itemId) => !isWeapon(itemId));
                        if (nonWeaponItems.length === 0) return null;
                        return (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-500">✓</span>
                            {nonWeaponItems.map(formatEquipmentName).join(' and ')}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Fixed Class Equipment */}
                {fixedEquipment.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-muted mb-2">
                      Class Equipment
                    </h4>
                    <ul className="space-y-1 text-sm text-text-secondary">
                      {consolidatedFixed.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Background Equipment */}
                {selectedBackground && (
                  <div>
                    <h4 className="text-sm font-medium text-text-muted mb-2">
                      Background Equipment
                    </h4>
                    <ul className="space-y-1 text-sm text-text-secondary">
                      {selectedBackground.equipment.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Custom Equipment */}
                {(draft.customEquipment?.length ?? 0) > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-accent-gold mb-2">
                      Custom Items
                    </h4>
                    <ul className="space-y-1 text-sm text-text-primary">
                      {(draft.customEquipment ?? []).map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-500">+</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              </div>
            ) : (
              <p className="text-sm text-text-muted text-center py-4">
                No equipment selected yet
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface EquipmentChoiceSelectorProps {
  choice: EquipmentChoice;
  selectedIndex: number;
  onChange: (index: number) => void;
}

function EquipmentChoiceSelector({
  choice,
  selectedIndex,
  onChange,
}: EquipmentChoiceSelectorProps) {
  // Filter out options that are all weapons
  const nonWeaponOptions = choice.from
    .map((option, index) => ({ option, originalIndex: index }))
    .filter(({ option }) => option.some((itemId) => !isWeapon(itemId)));

  if (nonWeaponOptions.length === 0) {
    return null;
  }

  return (
    <div className="border border-border-default rounded-lg p-3">
      <p className="text-sm text-text-muted mb-2">
        Choose {choice.choose === 1 ? 'one' : choice.choose}:
      </p>
      <div className="space-y-2">
        {nonWeaponOptions.map(({ option, originalIndex }) => (
          <button
            key={originalIndex}
            type="button"
            onClick={() => onChange(originalIndex)}
            className={clsx(
              'w-full text-left p-3 rounded-lg border text-sm transition-all duration-200',
              selectedIndex === originalIndex
                ? 'bg-accent-gold/10 border-accent-gold text-text-primary'
                : 'bg-background-secondary border-transparent hover:border-text-muted text-text-secondary'
            )}
          >
            <div className="flex items-center gap-2">
              <div
                className={clsx(
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                  selectedIndex === originalIndex
                    ? 'border-accent-gold'
                    : 'border-text-muted'
                )}
              >
                {selectedIndex === originalIndex && (
                  <div className="w-2 h-2 rounded-full bg-accent-gold" />
                )}
              </div>
              <span>{option.filter((itemId) => !isWeapon(itemId)).map(formatEquipmentName).join(' and ')}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Consolidate duplicate equipment items (e.g., 4x javelin -> "Javelin (4)")
function consolidateEquipment(items: string[]): string[] {
  const counts: Record<string, number> = {};
  for (const item of items) {
    counts[item] = (counts[item] || 0) + 1;
  }

  return Object.entries(counts).map(([item, count]) => {
    const name = formatEquipmentName(item);
    return count > 1 ? `${name} (${count})` : name;
  });
}
